import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import Dashboard from './pages/Dashboard.jsx';
import MyQRs from './pages/MyQRs.jsx';
import QREditor from './pages/QREditor.jsx';
import Settings from './pages/Settings.jsx';
import Orders from './pages/Orders.jsx';

// Mock user for VITE_MOCK_AUTH=true demo mode
const MOCK_SESSION_USER = { id: 'mock-001', name: 'DEMO OWNER', email: 'demo@qrqy.shop' };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true';

/**
 * useSession — fetches current user from GET /api/auth/me.
 *
 * When VITE_MOCK_AUTH=true: returns mock session immediately (no network call).
 * When VITE_API_BASE_URL is set: calls the real API, sends credentials (cookie).
 * When neither: user is null (unauthenticated).
 */
function useSession() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (MOCK_AUTH) {
      setUser(MOCK_SESSION_USER);
      setLoading(false);
      return;
    }

    if (!API_BASE_URL) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setUser(data?.user ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { user, loading };
}

function ProtectedRoute({ children }) {
  const { user, loading } = useSession();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0A0A0A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.4)',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.75rem',
        letterSpacing: '0.2em',
      }}>
        LOADING...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="my-qrs" element={<MyQRs />} />
          <Route path="qr/:code" element={<QREditor />} />
          <Route path="orders" element={<Orders />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
